// frontend/src/contexts/PriceContext.jsx
// Real-time Pi/USDT price from OKX WebSocket (trade-based, true last price)

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef
} from 'react';

const PriceContext = createContext();

// Fallback price if WebSocket fails
const FALLBACK_PI_USD = 0.20;
const RECONNECT_DELAY = 5000; // 5 seconds
const WS_URL = 'wss://ws.okx.com:8443/ws/v5/public';

export function PriceProvider({ children }) {
  const [piPrice, setPiPrice] = useState(FALLBACK_PI_USD);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [connected, setConnected] = useState(false);

  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    connectWebSocket();

    return () => {
      mountedRef.current = false;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);

  function connectWebSocket() {
    try {
      console.log('[OKX] Connecting to WebSocket...');
      const ws = new WebSocket(WS_URL);
      wsRef.current = ws;

      ws.onopen = () => {
        if (!mountedRef.current) return;

        console.log('[OKX] WebSocket connected');
        setConnected(true);
        setError(null);

        // Subscribe to PI-USDT trades (true last price)
        const subscribeMsg = {
          op: 'subscribe',
          args: [
            {
              channel: 'trades',
              instId: 'PI-USDT'
            }
          ]
        };

        ws.send(JSON.stringify(subscribeMsg));
        console.log('[OKX] Subscribed to PI-USDT trades');
      };

      ws.onmessage = (event) => {
        if (!mountedRef.current) return;

        try {
          const data = JSON.parse(event.data);

          // Subscription confirmation
          if (data.event === 'subscribe') {
            console.log('[OKX] Subscription confirmed');
            return;
          }

          // Trade data (real-time last price)
          if (
            data.arg &&
            data.arg.channel === 'trades' &&
            data.data &&
            data.data[0]
          ) {
            const trade = data.data[0];
            const price = parseFloat(trade.px);

            if (!Number.isNaN(price) && price > 0) {
              setPiPrice(price);
              setLoading(false);
              setError(null);
              console.log(`[OKX] Pi price updated: $${price.toFixed(4)}`);
            }
          }
        } catch (err) {
          console.error('[OKX] Error parsing message:', err);
        }
      };

      ws.onerror = (err) => {
        if (!mountedRef.current) return;

        console.error('[OKX] WebSocket error:', err);
        setError('Connection error');
        setConnected(false);
      };

      ws.onclose = () => {
        if (!mountedRef.current) return;

        console.log('[OKX] WebSocket closed');
        setConnected(false);

        // Attempt reconnection
        reconnectTimeoutRef.current = setTimeout(() => {
          if (mountedRef.current) {
            console.log('[OKX] Attempting to reconnect...');
            connectWebSocket();
          }
        }, RECONNECT_DELAY);
      };
    } catch (err) {
      console.error('[OKX] Failed to connect:', err);
      setError('Failed to connect');
      setConnected(false);
      setLoading(false);

      reconnectTimeoutRef.current = setTimeout(() => {
        if (mountedRef.current) {
          connectWebSocket();
        }
      }, RECONNECT_DELAY);
    }
  }

  const value = {
    piPrice,
    loading,
    error,
    connected,

    // Utility: Pi → USD
    piToUsd: (piAmount) => piAmount * piPrice,

    // Utility: USD → Pi
    usdToPi: (usdAmount) => usdAmount / piPrice
  };

  return (
    <PriceContext.Provider value={value}>
      {children}
    </PriceContext.Provider>
  );
}

// Hook to use Pi price in components
export function usePiPrice() {
  const context = useContext(PriceContext);
  if (!context) {
    throw new Error('usePiPrice must be used within PriceProvider');
  }
  return context;
}

// Utility function to format Pi amount
export function formatPi(amount) {
  if (typeof amount !== 'number') return '0 π';
  return `${amount.toFixed(6)} π`;
}

// Utility function to format USD amount
export function formatUsd(amount) {
  if (typeof amount !== 'number') return '$0.00';
  return `$${amount.toFixed(2)}`;
}
